<template>
  <div class="p-family-club__form-container">
    <Steps :steps="steps" :current-step="currentStep"></Steps>

    <form
      v-if="!showConfirmation"
      autocomplete="off"
      @submit.prevent="onSubmitForm">
      <Loader v-if="isLoading"></Loader>
      <Alert
        v-if="!isLoading && errorMsgs.length"
        key="submit-errors"
        dismiss-by-native=""
        type="danger"
        :messages="errorMsgs"
        @close="removeErrors"></Alert>

      <div class="b-block b-block--transparent b-block--sm-pad">
        <div :class="{ 'mb-48': showPasswordSection }">
          <div class="b-block__form-section mb-48">
            <!-- row -->
            <div class="form-floating">
              <input
                id="firstName"
                v-model.trim="firstName"
                :disabled="firstNameIsDisabled"
                name="firstName"
                type="text"
                class="form-control"
                :placeholder="dictionary.firstNamePlaceholder"
                :class="{ 'form-control--error': v$.firstName.$error }"
                @blur="v$.firstName.$touch" />

              <label for="firstName" class="form-label">
                {{ dictionary.firstNameLabel }}
              </label>

              <!-- validations -->
              <template v-if="v$.firstName.$error">
                <div
                  v-if="v$.firstName.required.$invalid"
                  class="field-validation-error">
                  {{ dictionary.firstNameRequired }}
                </div>
                <div
                  v-if="
                    v$.firstName.minLength.$invalid ||
                    v$.firstName.maxLength.$invalid
                  "
                  class="field-validation-error">
                  {{ dictionary.firstNameLength }}
                </div>
                <div
                  v-if="v$.firstName.regex.$invalid"
                  class="field-validation-error">
                  {{ dictionary.firstNameAlpha }}
                </div>
              </template>
            </div>

            <!-- row -->
            <div class="form-floating">
              <input
                id="lastName"
                v-model.trim="lastName"
                :disabled="lastNameIsDisabled"
                name="lastName"
                type="text"
                class="form-control"
                :placeholder="dictionary.lastNamePlaceholder"
                :class="{ 'form-control--error': v$.lastName.$error }"
                @blur="v$.lastName.$touch" />

              <label for="lastName" class="form-label">
                {{ dictionary.lastNameLabel }}
              </label>

              <!-- validations -->
              <template v-if="v$.lastName.$error">
                <div
                  v-if="v$.lastName.required.$invalid"
                  class="field-validation-error">
                  {{ dictionary.lastNameRequired }}
                </div>
                <div
                  v-if="
                    v$.lastName.minLength.$invalid ||
                    v$.lastName.maxLength.$invalid
                  "
                  class="field-validation-error">
                  {{ dictionary.lastNameLength }}
                </div>
                <div
                  v-if="v$.lastName.regex.$invalid"
                  class="field-validation-error">
                  {{ dictionary.lastNameAlpha }}
                </div>
              </template>
            </div>

            <!-- row -->
            <div class="form-floating">
              <input
                id="email"
                v-model.trim="email"
                :disabled="emailIsDisabled"
                name="email"
                type="email"
                class="form-control"
                :placeholder="dictionary.emailPlaceholder"
                :class="{ 'form-control--error': v$.email.$error }"
                formnovalidate
                @blur="v$.email.$touch" />

              <label for="email" class="form-label">
                {{ dictionary.emailLabel }}
              </label>

              <!-- validations -->
              <template v-if="v$.email.$error">
                <div
                  v-if="v$.email.required.$invalid"
                  class="field-validation-error">
                  {{ dictionary.emailRequired }}
                </div>
                <div
                  v-if="v$.email.email.$invalid"
                  class="field-validation-error">
                  {{ dictionary.emailRegex }}
                </div>
              </template>
            </div>

            <div class="b-block__flex">
              <div class="b-block__multiselect">
                <multiselect
                  id="areaCode"
                  v-model="areaCode"
                  name="areaCode"
                  :options="countryOptions"
                  :searchable="true"
                  :clear-on-search="false"
                  :can-clear="false"
                  :create-option="true"
                  :add-option-on="['tab', 'enter']"
                  :search-start="true"></multiselect>
              </div>
              <!-- row -->
              <div class="form-floating">
                <input
                  id="mobileTelephone"
                  v-model.trim="mobileTelephone"
                  name="mobileTelephone"
                  type="text"
                  class="form-control"
                  :placeholder="dictionary.mobileTelephonePlaceholder"
                  :class="{ 'form-control--error': v$.mobileTelephone.$error }"
                  @blur="v$.mobileTelephone.$touch"
                  @input="validateMobileTelephone" />

                <label for="mobileTelephone" class="form-label">
                  {{ dictionary.mobileTelephoneLabel }}
                </label>

                <!-- validations -->
                <template v-if="v$.mobileTelephone.$error">
                  <div
                    v-if="v$.mobileTelephone.required.$invalid"
                    class="field-validation-error">
                    {{ dictionary.mobileTelephoneRequired }}
                  </div>
                  <!-- <div
                    v-if="v$.mobileTelephone.regex.$invalid"
                    class="field-validation-error">
                    {{ dictionary.mobileTelephoneRegex }}
                  </div> -->
                </template>
              </div>
            </div>

            <!-- row -->
            <div class="form-floating">
              <select
                id="gender"
                v-model.trim="gender"
                name="gender"
                class="form-select">
                <option value="">{{ dictionary.genderPlaceholder }}</option>
                <option
                  v-for="item in genders"
                  :key="item.value"
                  :value="item.value"
                  :disabled="item.disabled">
                  {{ item.text }}
                </option>
              </select>

              <label for="gender" class="form-label">
                {{ dictionary.genderLabel }}
              </label>
            </div>

            <!-- row -->
            <div class="form-floating">
              <input
                id="zip"
                v-model.trim="zip"
                name="zip"
                type="text"
                class="form-control"
                :placeholder="dictionary.zipPlaceholder"
                :class="{ 'form-control--error': v$.zip.$error }"
                formnovalidate
                @blur="v$.zip.$touch" />

              <label for="zip" class="form-label">
                {{ dictionary.zipLabel }}
              </label>

              <!-- validations -->
              <template v-if="v$.zip.$error">
                <div
                  v-if="v$.zip.numeric.$invalid"
                  class="field-validation-error">
                  {{ dictionary.zipNumeric }}
                </div>
              </template>
            </div>

            <!-- row -->
            <div class="form-floating">
              <Datepicker
                v-model="dateOfBirth"
                uid="dateOfBirth"
                auto-apply
                name="dateOfBirth"
                :placeholder="dictionary.dateOfBirthPlaceholder"
                :enable-time-picker="false"
                :locale="configuration.locale"
                format="dd/MM/yyyy"
                autocomplete="off"
                model-type="yyyy-MM-dd"
                dark
                reverse-years
                prevent-min-max-navigation
                :hide-navigation="['month', 'year']"
                :timezone="{
                  convertModel: false,
                  exactMatch: true,
                  timezone: 'Europe/Athens',
                }"
                input-class-name="form-control form-control-icon"
                :year-range="[
                  new Date().getFullYear() - 80,
                  new Date().getFullYear() - 10,
                ]"
                :min-date="
                  new Date(
                    new Date().getFullYear() - 80,
                    new Date().getMonth(),
                    new Date().getDay()
                  )
                "
                :max-date="
                  new Date(
                    new Date().getFullYear() - 10,
                    new Date().getMonth(),
                    new Date().getDay()
                  )
                "
                :start-date="
                  new Date(
                    new Date().getFullYear() - 18,
                    new Date().getMonth(),
                    new Date().getDay()
                  )
                " />
              <label
                for="dp-input-dateOfBirth"
                class="form-label form-label-icon">
                {{ dictionary.dateOfBirthLabel }}
              </label>
            </div>
          </div>
        </div>

        <div v-if="showPasswordSection" class="b-block__form-section mb-0">
          <div class="b-block__mini-title">{{ dictionary.passwordsTitle }}</div>
          <!-- row -->
          <div class="form-floating">
            <input
              id="password"
              v-model.trim="password"
              name="password"
              :type="passwordInputType"
              class="form-control"
              autocomplete="new-password"
              :placeholder="dictionary.passwordPlaceholder"
              :class="{ 'form-control--error': v$.password.$error }"
              @blur="v$.password.$touch" />
            <label for="password" class="form-label">
              {{ dictionary.passwordLabel }}
            </label>
            <button
              class="form-floating__btn-password"
              :class="showPassword ? '' : 'form-floating__btn-password--seen'"
              type="button"
              @click="() => (showPassword = !showPassword)"></button>

            <!-- validations -->
            <template v-if="v$.password.$error">
              <div
                v-if="v$.password.required.$invalid"
                class="field-validation-error">
                {{ dictionary.passwordRequired }}
              </div>
              <div
                v-if="v$.password.regex.$invalid"
                class="field-validation-error">
                {{ dictionary.passwordRegex }}
              </div>
            </template>
          </div>

          <!-- row -->
          <div class="form-floating">
            <input
              id="password-confirm"
              v-model.trim="passwordConfirm"
              name="password-confirm"
              type="password"
              class="form-control"
              :placeholder="dictionary.passwordConfirmPlaceholder"
              :class="{ 'form-control--error': v$.passwordConfirm.$error }"
              @blur="v$.passwordConfirm.$touch"
              @paste="(e) => e.preventDefault()"
              @drop="(e) => e.preventDefault()" />

            <label for="password-confirm" class="form-label">
              {{ dictionary.passwordConfirmLabel }}
            </label>

            <!-- validations -->
            <template v-if="v$.passwordConfirm.$error">
              <div
                v-if="v$.passwordConfirm.sameAspassword.$invalid"
                class="field-validation-error">
                {{ dictionary.passwordConfirmMatch }}
              </div>
            </template>
          </div>

          <div class="b-info-message">
            <div
              class="b-info-message__content"
              v-html="dictionary.passwordInfo"></div>
          </div>

          <div class="p-contact__form-message">
            {{ dictionary.fieldRequired }}
          </div>
        </div>
      </div>

      <div class="b-block__checkboxes">
        <div
          v-if="!configuration.hideContactByThirdPartyCheckbox"
          class="custom-chck custom-chck--additional-style">
          <input
            id="contactByThirdParty"
            v-model="contactByThirdParty"
            type="checkbox"
            name="contactByThirdParty" />
          <label class="custom-chck__span" for="contactByThirdParty">
            {{ dictionary.contactByThirdPartyLabel }}
          </label>
        </div>

        <div class="custom-chck custom-chck--additional-style">
          <input
            id="accept-terms"
            v-model="acceptTerms"
            type="checkbox"
            name="accept-terms"
            :class="{ 'form-control--error': v$.acceptTerms.$error }"
            @click="
              (e) => {
                v$.acceptTerms.$touch
              }
            " />

          <label for="accept-terms">
            <span class="custom-chck__span">
              <div class="custom-chck__text">
                <em>{{ dictionary.acceptTermsTitle }}</em>
                <div
                  v-if="dictionary.acceptTermsDesc"
                  v-html="dictionary.acceptTermsDesc"></div>
              </div>
            </span>
          </label>

          <!-- validations -->
          <template v-if="v$.acceptTerms.$error">
            <div
              v-if="v$.acceptTerms.required.$invalid"
              class="field-validation-error">
              {{ dictionary.acceptTermsRequired }}
            </div>
          </template>
        </div>
      </div>

      <div class="b-block__form-btn">
        <button
          type="submit"
          class="p-contact__submit-btn"
          :disabled="disabledBtn">
          <span>{{ dictionary.buttonSubmitText }}</span>
        </button>
      </div>
    </form>

    <ConfirmationForm
      v-if="showConfirmation"
      :first-name="firstName"
      :last-name="lastName"
      :email="email"
      :area-code="areaCode"
      :mobile-telephone="mobileTelephone"
      :date-of-birth="dateOfBirth"
      :zip="zip"
      :gender="gender"
      :contact-by-third-party="contactByThirdParty"
      :accept-terms="acceptTerms"
      :country-options="countryOptions"
      :non-greek-area-code="nonGreekAreaCode"
      @changed-email="getNewEmail"
      @changed-phone="getNewPhone"
      @changed-country-code="getCode"></ConfirmationForm>

    <ModalOverlay
      v-model="showSuccessModal"
      no-close-backdrop
      background-class="b-pop-up--alert-success b-pop-up__holder"
      header-background-class="b-pop-up__header"
      :hide-close-btn="hideModalBtn"
      @close="() => toggleSuccessModal(false)">
      <template #header>
        <div class="b-pop-up__header__title">
          {{ !isCurrentClubPaid ? dictionary.submitSuccessHeader : '' }}
        </div>
      </template>
      <template #body>
        <div class="b-pop-up__alert-success__content">
          <div class="b-pop-up__alert-success__icon" :class="modalIcon"></div>
          <div class="b-pop-up__alert-success__content__container">
            <div class="b-pop-up__alert-success__title">
              {{ modalTitle }}
            </div>
            <div
              v-if="!isCurrentClubPaid"
              class="b-pop-up__alert-success__text">
              {{ dictionary.submitSuccessContent }}
            </div>

            <Link
              v-if="isCurrentClubPaid"
              class="b-pop-up__alert-success__cta"
              :url="errorModalLink.url"
              :target="errorModalLink.target"
              :title="errorModalLink.title" />
          </div>
        </div>
      </template>
    </ModalOverlay>

    <!--new club-->
    <ModalOverlay
      v-model="showNewClubModal"
      no-close-backdrop
      background-class="b-pop-up--alert-success b-pop-up__holder"
      header-background-class="b-pop-up__header"
      :hide-close-btn="hideModalBtn"
      @close="() => toggleNewClubModal(false)">
      <template #header>
        <div class="b-pop-up__header__title">
          {{ newClubModal.label }}
        </div>
      </template>
      <template #body>
        <div class="b-pop-up__alert-success__content">
          <div class="b-pop-up__alert-success__content__container">
            <div class="b-pop-up__alert-success__title">
              {{ newClubModal.title }}
            </div>
            <div class="b-pop-up__alert-success__text">
              {{ newClubModal.desc }}
            </div>

            <div class="b-pop-up__cta-area">
              <button
                class="btn-primary-orange btn--block"
                type="button"
                @click="goNextStep">
                <span>{{ newClubModal.confirmBtnLabel }}</span>
              </button>
              <Link
                class="b-pop-up__alert-success__cta"
                :url="newClubModal.cancelUrl.url"
                :title="newClubModal.cancelUrl.label" />
            </div>
          </div>
        </div>
      </template>
    </ModalOverlay>

    <!--existing club-->
    <ModalOverlay
      v-model="showExistingClubModal"
      no-close-backdrop
      background-class="b-pop-up--alert-success b-pop-up__holder"
      header-background-class="b-pop-up__header"
      :hide-close-btn="hideModalBtn"
      @close="() => toggleExistingClubModal(false)">
      <template #header>
        <div class="b-pop-up__header__title">
          {{ existingClubModal.label }}
        </div>
      </template>
      <template #body>
        <div class="b-pop-up__alert-success__content">
          <div class="b-pop-up__alert-success__content__container">
            <div class="b-pop-up__alert-success__title">
              {{ existingClubModal.title }}
            </div>
            <div class="b-pop-up__alert-success__text">
              {{ existingClubModal.desc }}
            </div>

            <div class="b-pop-up__cta-area">
              <Link
                class="b-pop-up__alert-success__cta"
                :url="existingClubModal.cancelUrl.url"
                :title="existingClubModal.cancelUrl.label" />

              <a
                :href="existingClubModal.confirmBtnLabel.url"
                class="btn-primary-orange">
                <span>
                  {{ existingClubModal.confirmBtnLabel.label }}
                </span>
              </a>
            </div>
          </div>
        </div>
      </template>
    </ModalOverlay>
  </div>
</template>

<script>
// STORE

import * as lib from './../../lib/lib'
import constants from '../../lib/constants'
import Loader from './../components/Shared/Loader.vue'
import Alert from './../components/Shared/Alert.vue'
import useVuelidate from '@vuelidate/core'
import ModalOverlay from './../components/ModalOverlay.vue'
import postRequestHandler from '../../lib/postRequestHandler'
import getErrorMessage from '../../lib/getErrorMessage'
import Link from './../components/UI/Link.vue'
import Datepicker from '@vuepic/vue-datepicker'
import Steps from './../components/RegisterForm/Steps.vue'
import ConfirmationForm from '../components/RegisterForm/ConfirmationForm.vue'
import jquery from 'jquery'
import Multiselect from '@vueform/multiselect'

const { dialingCodes, regex } = constants
const { telephoneGreek, telephoneInternational, greekLatinSpace, password } =
  regex

import {
  email,
  helpers,
  maxLength,
  minLength,
  numeric,
  required,
  sameAs,
} from '@vuelidate/validators'

export default {
  /* eslint-disable vue/no-reserved-component-names */
  components: {
    Loader,
    ModalOverlay,
    Alert,
    Link,
    Datepicker,
    Steps,
    ConfirmationForm,
    Multiselect,
  },
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      isLoading: true,
      errorMsgs: [],
      showSuccessModal: false,
      showNewClubModal: false,
      showExistingClubModal: false,
      showErrorModal: false,
      showPassword: false,

      firstNameIsDisabled: false,
      lastNameIsDisabled: false,
      emailIsDisabled: false,
      // personal info
      firstName: '',
      lastName: '',
      mobileTelephone: '',
      email: '',
      isCurrentClubPaid: false,
      // address info
      zip: '',
      dateOfBirth: '',
      contactByThirdParty: false,
      gender: '',
      genders: [],
      // change password
      password: '',
      passwordConfirm: '',
      // terms
      acceptTerms: false,
      acceptTermsTitleAndText: `<em>${this.dictionary.acceptTermsTitle}</em>'${this.dictionary.acceptTermsLinkText}`,
      errorModalLink: null,
      newClubModal: null,
      existingClubModal: null,
      steps: [],
      currentStep: 0,
      showConfirmation: false,
      areaCode: dialingCodes.greece,
      nonGreekAreaCode: false,
      countryOptions: null,
    }
  },
  validations() {
    const validateMobileTelephone = (value) => {
      const telephoneRegex =
        this.areaCode === dialingCodes.greece
          ? telephoneGreek
          : telephoneInternational

      return !helpers.req(value) || telephoneRegex.test(value)
    }

    return {
      // personal info
      firstName: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(40),
        regex: helpers.regex(greekLatinSpace),
        // regex: helpers.regex(greekLatin),
      },
      lastName: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(40),
        regex: helpers.regex(greekLatinSpace),
        // regex: helpers.regex(greekLatin),
      },
      mobileTelephone: {
        required,
        regex: validateMobileTelephone,
      },
      email: {
        required,
        email,
      },
      zip: {
        numeric,
      },
      // change password
      password:
        this.configuration.isSocial || this.configuration.isUpgrade
          ? {}
          : {
              required,
              regex: helpers.regex(password),
            },
      passwordConfirm:
        this.configuration.isSocial || this.configuration.isUpgrade
          ? {}
          : {
              sameAspassword: sameAs(this.password),
            },
      // terms
      acceptTerms: {
        required: sameAs(true),
      },
    }
  },

  computed: {
    showPasswordSection() {
      const { isSocial, isUpgrade } = this.configuration
      return !isSocial && !isUpgrade
    },
    passwordInputType() {
      return this.showPassword ? 'text' : 'password'
    },
    passwordHasValue() {
      return typeof this.password === 'string' && this.password.length > 0
    },
    disabledBtn() {
      return this.isCurrentClubPaid ? true : false
    },

    modalIcon() {
      return this.isCurrentClubPaid
        ? 'b-pop-up__alert-success__icon--warning'
        : 'b-pop-up__alert-success__icon--success'
    },

    modalTitle() {
      return this.isCurrentClubPaid
        ? this.dictionary.errorModalTitle
        : this.dictionary.submitSuccessTitle
    },

    hideModalBtn() {
      return this.isCurrentClubPaid ? true : false
    },
  },

  watch: {
    mobileTelephone(newVal) {
      const areaCode = this.areaCode
      const regex = new RegExp(`^\\+?${areaCode}`)

      if (newVal.match(regex)) {
        this.mobileTelephone = newVal.replace(regex, '')
      }
    },
    areaCode(newVal) {
      if (newVal) {
        const regex = new RegExp(`^\\+?${newVal}`)
        if (this.mobileTelephone.match(regex)) {
          this.mobileTelephone = this.mobileTelephone.replace(regex, '')
        }
      }
    },
  },

  mounted() {
    const { errorModalLink, steps, newClubModal, existingClubModal } =
      lib.getData('familyClubUserRegisterCompleteFormAppData')

    const { isCurrentClubPaid, countryOptions } = this.configuration

    this.errorModalLink = errorModalLink
    this.isCurrentClubPaid = isCurrentClubPaid
    this.newClubModal = newClubModal
    this.existingClubModal = existingClubModal
    this.steps = steps
    this.countryOptions = [...(countryOptions || [])].sort((a, b) =>
      String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    )

    lib.checkCaptcha(() => (this.isLoading = false))

    this.setPreselectedOptions()
    this.setUserRegisterData()
    // disable fields with prefetched data
    this.setDisabledFields()

    this.$nextTick(() => {
      this.toggleSuccessModal(this.isCurrentClubPaid)
    })
  },

  methods: {
    removeErrors() {
      this.errorMsgs = []
    },

    validateMobileTelephone() {
      this.mobileTelephone = this.mobileTelephone.replace(/[^0-9]/g, '')
    },

    getNewEmail(newEmail) {
      this.email = newEmail
    },

    getNewPhone(newPhone) {
      this.mobileTelephone = newPhone
    },

    getCode(newCode) {
      this.areaCode = newCode
    },

    toggleSuccessModal(show) {
      this.showSuccessModal = show
    },

    toggleNewClubModal(show) {
      this.showNewClubModal = show
    },

    toggleExistingClubModal(show) {
      this.showExistingClubModal = show
    },

    setPreselectedOptions() {
      const { genders } = lib.getData(
        'familyClubUserRegisterCompleteFormAppData'
      )
      this.genders = genders
      // genders
      const entry = genders.find((i) => i.selected)
      if (entry) {
        this.gender = entry.value
      }
    },
    setUserRegisterData() {
      const { userRegisterData } = lib.getData(
        'familyClubUserRegisterCompleteFormAppData'
      )
      Object.assign(this.$data, userRegisterData)
    },

    setDisabledFields() {
      this.firstNameIsDisabled = !!this.firstName && this.showPasswordSection
      this.lastNameIsDisabled = !!this.lastName && this.showPasswordSection
      this.emailIsDisabled = !!this.email
    },

    resetDefaultFormData() {
      document.activeElement.blur()
      this.v$.$reset()
      this.password = ''
      this.passwordConfirm = ''
      // this.acceptTerms = false
    },

    async onSubmitForm() {
      this.v$.$validate()

      if (this.isLoading) {
        return false
      }

      if (this.v$.$invalid) {
        await this.$nextTick()
        lib.scrollToValidation()
        return false
      }

      this.removeErrors()
      this.isLoading = true

      const postData = {
        userSessionId: this.configuration.userSessionId,
        areaCode: this.areaCode,
        mobileTelephone: this.mobileTelephone,
        email: this.email,
        zip: this.zip,
        password: this.password,
        passwordConfirm: this.passwordConfirm,
        acceptTerms: this.acceptTerms,
        gender: this.gender,
        firstName: this.firstName,
        lastName: this.lastName,
        contactByThirdParty: this.contactByThirdParty,
        dateOfBirth: this.dateOfBirth === null ? '' : this.dateOfBirth,
        clubId: this.configuration.clubId,
        language: document.documentElement.lang,
      }

      await postRequestHandler({
        postData,
        endpoint: this.configuration.endpointSubmitData,
        successCallback: this.handleSuccess,
        errorCallback: this.handleError,
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.startCaptchaAction,
      })
    },

    async handleSuccess(data) {
      this.isLoading = false
      this.errorMsgs = []
      this.resetDefaultFormData()

      if (data.data.newClub) {
        this.toggleNewClubModal(true)
      } else if (data.data.existingEmail) {
        this.toggleExistingClubModal(true)
      } else {
        this.nonGreekAreaCode = !!data?.data?.nonGreekAreaCode
        this.showConfirmation = true
        this.nextStep()
        await this.$nextTick()
        lib.scrollToElem(jquery('.p-family-club'), 0)
      }
    },

    async handleError(messages) {
      this.isLoading = false

      this.errorMsgs = getErrorMessage(
        messages,
        this.dictionary.defaultErrorMessage
      )

      await this.$nextTick()
      lib.scrollToError()
    },

    async goNextStep() {
      this.showConfirmation = true
      this.toggleNewClubModal(false)
      this.nextStep()
      await this.$nextTick()
      lib.scrollToElem(jquery('.p-family-club'), 0)
    },

    nextStep() {
      this.currentStep += 1
    },
  },
}
</script>
